import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { PathType } from "@rosette/ts/src/royalur/model/path/PathType.js";
import { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { RoyalUrDice } from "@rosette/ts/src/business/game/royalur/RoyalUrDice.js";


export interface GamePageParamValues {
    settings: KnownLobbySettings;
    settingsAreUnknown?: boolean;
    tab: string | null;
}


function parseIntOrNull(value: string | null): number | null {
    if (value === null)
        return null;

    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
}


function encodeBool(value: boolean): string {
    return value ? "y" : "n";
}


function parseBoolOrNull(value: string | null): boolean | null {
    if (value === "y")
        return true;
    if (value === "n")
        return false;
    return null;
}


export function updateParamValues(
    searchParams: URLSearchParams,
    values: GamePageParamValues,
) {
    const original = searchParams.toString();
    const newSearchParams = new URLSearchParams(original);

    const settings = values.settings;
    if (settings) {
        newSearchParams.set("mode", settings.getMode().getID());
        newSearchParams.set("rules", settings.getRuleset().getID());
        newSearchParams.set("bot", settings.getBotType().getID());
    } else {
        newSearchParams.delete("mode");
        newSearchParams.delete("rules");
        newSearchParams.delete("bot");
    }

    if (settings && !settings.getRuleset().hasSettings()) {
        const customSettings = settings.getCustomGameSettings();
        newSearchParams.set("dice", customSettings.getDice().getID());
        newSearchParams.set("path", customSettings.getPaths().getID());
        newSearchParams.set("pieces", `${customSettings.getStartingPieceCount()}`);
        newSearchParams.set(
            "safe_rosettes",
            encodeBool(customSettings.areRosettesSafe()),
        );
        newSearchParams.set(
            "rosettes_reroll",
            encodeBool(customSettings.doRosettesGrantExtraRolls()),
        );
        newSearchParams.set(
            "captures_reroll",
            encodeBool(customSettings.doCapturesGrantExtraRolls()),
        );
    } else {
        newSearchParams.delete("dice");
        newSearchParams.delete("path");
        newSearchParams.delete("pieces");
        newSearchParams.delete("safe_rosettes");
        newSearchParams.delete("rosettes_reroll");
        newSearchParams.delete("captures_reroll");
    }

    const tab = values.tab;
    if (tab) {
        newSearchParams.set("tab", tab);
    } else {
        newSearchParams.delete("tab");
    }

    const modified = newSearchParams.toString();
    if (original !== modified) {
        window.history.replaceState(null, "", `?${modified}`);
    }
}


export function readGamePageParamValues(searchParams: URLSearchParams): GamePageParamValues {
    const defaultSettings = KnownLobbySettings.DEFAULT;

    const tab = searchParams.get("tab");
    const modeParam = searchParams.get("mode");
    const rulesetParam = searchParams.get("rules");
    const botParam = searchParams.get("bot");

    const mode = GameMode.getByIDOrNull(modeParam) ?? defaultSettings.getMode();
    const ruleset = Ruleset.getByIDOrNull(rulesetParam) ?? defaultSettings.getRuleset();
    const bot = BotType.getByIDOrNull(botParam) ?? defaultSettings.getBotType();

    const customDice = searchParams.get("dice");
    const customPath = searchParams.get("path");
    const customStartingPieceCount = searchParams.get("pieces");
    const customSafeRosettes = searchParams.get("safe_rosettes");
    const customRosettesGrantExtraRolls = searchParams.get("rosettes_reroll");
    const customCapturesGrantExtraRolls = searchParams.get("captures_reroll");

    let customGameSettings = defaultSettings.getRuleset().getSettings();
    if (ruleset.hasSettings()) {
        customGameSettings = ruleset.getSettings();
    } else {
        const diceType = RoyalUrDice.getByIDOrNull(customDice);
        const pathType = PathType.getByIDOrNull(customPath);
        const startingPieceCount = parseIntOrNull(customStartingPieceCount);
        const safeRosettes = parseBoolOrNull(customSafeRosettes);
        const rosettesGrantExtraRolls = parseBoolOrNull(customRosettesGrantExtraRolls);
        const capturesGrantExtraRolls = parseBoolOrNull(customCapturesGrantExtraRolls);

        if (diceType !== null) {
            customGameSettings = customGameSettings.withDice(diceType);
        }
        if (pathType !== null) {
            customGameSettings = customGameSettings.withPaths(pathType);
        }
        if (startingPieceCount !== null) {
            customGameSettings = customGameSettings.withStartingPieceCount(startingPieceCount);
        }
        if (safeRosettes !== null) {
            customGameSettings = customGameSettings.withSafeRosettes(safeRosettes);
        }
        if (rosettesGrantExtraRolls !== null) {
            customGameSettings = customGameSettings.withRosettesGrantExtraRolls(
                rosettesGrantExtraRolls,
            );
        }
        if (capturesGrantExtraRolls !== null) {
            customGameSettings = customGameSettings.withCapturesGrantExtraRolls(
                capturesGrantExtraRolls,
            );
        }
    }

    const settings = new KnownLobbySettings(
        mode, ruleset, customGameSettings, bot,
    );
    const settingsAreUnknown = (!modeParam && !rulesetParam && !botParam);

    return {
        settings,
        settingsAreUnknown,
        tab,
    };
}
